import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Color } from '~/constants/css';
import { useAppContext, useKeyContext } from '~/contexts';
import { isMobile } from '~/helpers';
import { User } from '~/types';
import UserPopup from '~/components/UserPopup';
const deviceIsMobile = isMobile(navigator);
const deletedLabel = 'Deleted';

export default function UsernameText({
  className,
  color,
  onMenuShownChange,
  style = {},
  textStyle = {},
  user = {
    id: 0,
    username: '',
    profilePicUrl: '',
    profileTheme: '',
    profileFirstRow: '',
    rank: 0,
    twinkleXP: 0,
    xpThisMonth: 0
  },
  wordBreakEnabled,
  displayedName,
  wordMasterContext,
  wordMasterPoints,
  wordMasterLabel,
  activityContext,
  activityPoints
}: {
  className?: string;
  color?: string;
  onMenuShownChange?: (v: boolean) => void;
  style?: object;
  textStyle?: React.CSSProperties;
  user?: User;
  wordBreakEnabled?: boolean;
  displayedName?: string;
  wordMasterContext?: boolean;
  wordMasterPoints?: number;
  wordMasterLabel?: string;
  activityContext?: string;
  activityPoints?: number;
}) {
  const [loading, setLoading] = useState(false);
  const cachedLoaded = useAppContext(
    (v) => v.user.state.userObj[user.id]?.loaded
  );
  const cachedLevel = useAppContext(
    (v) => v.user.state.userObj[user.id]?.level
  );
  const cachedTwinkleXP = useAppContext(
    (v) => v.user.state.userObj[user.id]?.twinkleXP
  );
  const cachedUnlockedAchievementIds = useAppContext(
    (v) => v.user.state.userObj[user.id]?.unlockedAchievementIds
  );
  const cachedChessMaxLevelUnlocked = useAppContext(
    (v) => v.user.state.userObj[user.id]?.chessMaxLevelUnlocked
  );
  const showTimerRef: React.RefObject<any> = useRef(0);
  const hideTimerRef: React.RefObject<any> = useRef(0);
  const hideTimerRef2: React.RefObject<any> = useRef(0);
  const UsernameTextRef = useRef<HTMLDivElement | null>(null);
  const mouseEntered = useRef(false);
  const loadProfile = useAppContext((v) => v.requestHelpers.loadProfile);
  const onSetUserState = useAppContext((v) => v.user.actions.onSetUserState);

  const userId = useKeyContext((v) => v.myState.userId);
  const [dropdownContext, setDropdownContext] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const menuShownRef = useRef(false);

  const shouldLoadProfile = useMemo(() => {
    return (
      (cachedTwinkleXP == null && user.twinkleXP == null) ||
      cachedLevel == null ||
      (!cachedUnlockedAchievementIds && !user.unlockedAchievementIds) ||
      (!cachedLoaded &&
        cachedChessMaxLevelUnlocked == null &&
        user.chessMaxLevelUnlocked == null)
    );
  }, [
    cachedChessMaxLevelUnlocked,
    cachedLevel,
    cachedLoaded,
    cachedTwinkleXP,
    cachedUnlockedAchievementIds,
    user.chessMaxLevelUnlocked,
    user.twinkleXP,
    user.unlockedAchievementIds
  ]);

  useEffect(() => {
    menuShownRef.current = !!dropdownContext;
    onMenuShownChange?.(!!dropdownContext);
  }, [dropdownContext, onMenuShownChange]);

  return (
    <div
      ref={UsernameTextRef}
      data-feed-card-interactive="true"
      style={{
        display: 'inline',
        ...(dropdownContext
          ? {}
          : { overflowX: 'hidden', textOverflow: 'ellipsis' }),
        position: 'relative',
        ...style
      }}
      className={className}
      onMouseLeave={deviceIsMobile ? undefined : handleMouseLeave}
    >
      <div
        style={{
          width: '100%',
          display: 'inline'
        }}
      >
        <div
          style={{
            display: 'inline',
            cursor: 'pointer',
            fontWeight: 'bold',
            touchAction: 'manipulation',
            ...(wordBreakEnabled
              ? { overflowWrap: 'break-word', wordBreak: 'break-word' }
              : {}),
            color: user.username
              ? color || Color.darkerGray()
              : Color.lighterGray(),
            ...textStyle
          }}
          onClick={onUsernameClick}
          onMouseEnter={onMouseEnter}
        >
          {displayedName || user.username || `(${deletedLabel})`}
        </div>
      </div>
      {dropdownContext && (
        <UserPopup
          isLoading={loading}
          triggerRef={UsernameTextRef}
          popupContext={dropdownContext}
          onMouseEnter={() => {
            clearTimeout(hideTimerRef.current);
            clearTimeout(hideTimerRef2.current);
          }}
          onMouseLeave={() => {
            hideTimerRef2.current = setTimeout(() => {
              setDropdownContext(null);
            }, 500);
          }}
          myId={userId}
          user={user}
          onHide={() => setDropdownContext(null)}
          onSetPopupContext={setDropdownContext}
          wordMasterContext={wordMasterContext}
          wordMasterPoints={wordMasterPoints}
          wordMasterLabel={wordMasterLabel}
          activityContext={activityContext}
          activityPoints={activityPoints}
        />
      )}
    </div>
  );

  async function onMouseEnter() {
    mouseEntered.current = true;
    const parentElementDimensions =
      UsernameTextRef.current?.getBoundingClientRect?.() || {
        x: 0,
        y: 0,
        width: 0,
        height: 0
      };
    if (user.username && !deviceIsMobile) {
      clearTimeout(hideTimerRef.current);
      clearTimeout(hideTimerRef2.current);
      clearTimeout(showTimerRef.current);
      if (shouldLoadProfile) {
        setLoading(true);
        showTimerRef.current = setTimeout(async () => {
          if (mouseEntered.current) {
            setDropdownContext(parentElementDimensions);
          }
        }, 500);
        const data = await loadProfile(user.id);
        onSetUserState({
          userId: user.id,
          newState: { ...data, loaded: true }
        });
        setLoading(false);
      } else {
        showTimerRef.current = setTimeout(
          () => setDropdownContext(parentElementDimensions),
          500
        );
      }
    }
  }

  async function onUsernameClick(event: React.MouseEvent<HTMLDivElement>) {
    event.stopPropagation();
    const elementContext = {
      x: UsernameTextRef.current?.getBoundingClientRect?.()?.left || 0,
      y: UsernameTextRef.current?.getBoundingClientRect?.()?.top || 0,
      width: UsernameTextRef.current?.getBoundingClientRect?.()?.width || 0,
      height: UsernameTextRef.current?.getBoundingClientRect?.()?.height || 0
    };
    if (user.username) {
      if (shouldLoadProfile && !menuShownRef.current) {
        setLoading(true);
        setDropdownContext(elementContext);
        const data = await loadProfile(user.id);
        onSetUserState({
          userId: user.id,
          newState: { ...data, loaded: true }
        });
        setLoading(false);
      } else {
        setDropdownContext(menuShownRef.current ? null : elementContext);
      }
    }
  }

  function handleMouseLeave() {
    clearTimeout(showTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setDropdownContext(null);
    }, 500);
  }
}
